/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { SavedOutfit, UserProfile, Lookbook } from '../types';
import { useTranslation } from '../i18n/useTranslation';
import { hexToHsl } from '../lib/utils';
import WardrobeEquityDashboard from './WardrobeEquityDashboard';

interface StyleAnalyticsDashboardProps {
    savedOutfits: SavedOutfit[];
    lookbooks: Lookbook[];
    favoritedOutfitIds: Set<string>;
    userProfile: UserProfile;
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const getColorGroup = (hex: string): string => {
    const { h, s, l } = hexToHsl(hex);
    if (s < 15 || l < 10 || l > 90) { // Neutrals
        if (l < 10) return 'color_group_black';
        if (l > 90) return 'color_group_white';
        if (s < 10) return 'color_group_gray';
        return 'color_group_brown';
    }
    if (h >= 340 || h < 20) return 'color_group_red';
    if (h >= 20 && h < 45) return 'color_group_orange';
    if (h >= 45 && h < 70) return 'color_group_yellow';
    if (h >= 70 && h < 160) return 'color_group_green';
    if (h >= 160 && h < 260) return 'color_group_blue';
    if (h >= 260 && h < 300) return 'color_group_purple';
    if (h >= 300 && h < 340) return 'color_group_pink';
    return 'color_group_other';
};

const StyleAnalyticsDashboard: React.FC<StyleAnalyticsDashboardProps> = ({ savedOutfits, lookbooks, favoritedOutfitIds }) => {
    const { t } = useTranslation();

    const analyticsData = useMemo(() => {
        const colorCounts: Record<string, number> = {};
        const categoryCounts: Record<string, number> = {};
        let totalItems = 0;

        savedOutfits.forEach(outfit => {
            const colors = [outfit.layer.appliedMainGarmentColor, ...Object.values(outfit.layer.appliedAccessoryColors)].filter(Boolean) as string[];
            colors.forEach(color => {
                const group = getColorGroup(color);
                colorCounts[group] = (colorCounts[group] || 0) + 1;
            });

            const categories = [outfit.layer.mainGarment?.category, ...outfit.layer.accessories.map(a => a.category)].filter(Boolean) as string[];
            categories.forEach(cat => {
                categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                totalItems++;
            });
        });

        const sortedColors = Object.entries(colorCounts).sort((a, b) => b[1] - a[1]);
        const sortedCategories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);

        return {
            totalItems,
            colorCounts: sortedColors,
            categoryCounts: sortedCategories,
        };
    }, [savedOutfits]);

    if (savedOutfits.length === 0) {
        return (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                <p>{t('styleAnalytics_noData')}</p>
            </div>
        );
    }

    const colorKeyToHex: { [key: string]: string } = {
        color_group_black: '#000000',
        color_group_white: '#FFFFFF',
        color_group_gray: '#808080',
        color_group_brown: '#A52A2A',
        color_group_red: '#FF0000',
        color_group_orange: '#FFA500',
        color_group_yellow: '#FFFF00',
        color_group_green: '#008000',
        color_group_blue: '#0000FF',
        color_group_purple: '#800080',
        color_group_pink: '#FFC0CB',
        color_group_other: '#4B0082', // Indigo as a fallback
    };


    return (
        <div className="space-y-8">
            <WardrobeEquityDashboard savedOutfits={savedOutfits} />

            <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border dark:border-gray-700">
                <h3 className="text-xl font-serif font-semibold mb-4">{t('styleAnalytics_overallStats')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{savedOutfits.length}</p>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('styleAnalytics_totalOutfits')}</p>
                    </div>
                    <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{lookbooks.length}</p>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('styleAnalytics_totalLookbooks')}</p>
                    </div>
                    <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg col-span-2 md:col-span-1">
                        <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{favoritedOutfitIds.size}</p>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('styleAnalytics_favoriteOutfits')}</p>
                    </div>
                </div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border dark:border-gray-700 col-span-2">
                <h3 className="text-xl font-serif font-semibold mb-4 flex items-center gap-2">
                    {t('styleAnalytics_colorDNA') || "Visual Wardrobe Insights"}
                </h3>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(20px,1fr))] gap-0.5 rounded-lg overflow-hidden h-32 w-full">
                    {/* Color DNA Visualization */}
                    {savedOutfits.flatMap(outfit =>
                        [outfit.layer.appliedMainGarmentColor, ...Object.values(outfit.layer.appliedAccessoryColors)]
                            .filter(Boolean)
                    ).map((color, idx) => (
                        <div
                            key={idx}
                            style={{ backgroundColor: color as string }}
                            className="w-full h-full hover:scale-110 hover:z-10 transition-transform"
                            title={color as string}
                        />
                    ))}
                    {savedOutfits.length === 0 && <div className="col-span-full text-center text-gray-400 p-8 border-2 border-dashed rounded-lg">No colors analyzable yet</div>}
                </div>
                <p className="text-sm text-gray-500 mt-2 italic text-center text-gray-600 dark:text-gray-400">
                    {t('styleAnalytics_dna_desc') || "Your wardrobe's color DNA based on outfit usage."}
                </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border dark:border-gray-700">
                <h3 className="text-xl font-serif font-semibold mb-4">{t('styleAnalytics_colorPalette')}</h3>
                <div className="flex flex-wrap gap-4">
                    {analyticsData.colorCounts.map(([colorKey, count]) => {
                        const percentage = analyticsData.totalItems > 0 ? ((count / analyticsData.totalItems) * 100).toFixed(1) : "0.0";
                        const colorName = t(colorKey);
                        return (
                            <div key={colorKey} className="flex items-center gap-2" title={`${colorName}: ${percentage}%`}>
                                <div className="w-6 h-6 rounded-full border dark:border-gray-600" style={{ backgroundColor: colorKeyToHex[colorKey] || '#ccc' }} />
                                <div>
                                    <p className="font-semibold text-sm">{colorName}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{percentage}%</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border dark:border-gray-700">
                <h3 className="text-xl font-serif font-semibold mb-4">{t('styleAnalytics_categoryDistribution')}</h3>
                <div className="space-y-3">
                    {analyticsData.categoryCounts.map(([category, count]) => {
                        const percentage = analyticsData.totalItems > 0 ? (count / analyticsData.totalItems) * 100 : 0;
                        return (
                            <div key={category}>
                                <div className="flex justify-between mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    <span>{t(`wardrobe_category_${category}` as any, undefined) || category}</span>
                                    <span>{count}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                    <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${percentage}%` }}></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </motion.div>
        </div>
    );
};

export default StyleAnalyticsDashboard;
